import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Video, Heart, Eye, ImageIcon, Star, Award, ArrowLeft, Filter, MessageCircle, Send, X, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import authService from '../services/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Gallery = () => {
  const navigate = useNavigate();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => {
    fetchMedia();
    checkUser();
  }, []);

  const checkUser = async () => {
    try {
      const user = await authService.getCurrentUser();
      if (user) {
        setCurrentUser(user);
      } else {
        const stored = authService.getStoredUser();
        if (stored) setCurrentUser(stored);
      }
    } catch (error) {
      console.error('Error checking user:', error);
    }
  };

  const fetchMedia = async () => {
    try {
      const response = await fetch(`${API_URL}/api/media?limit=50`);
      const data = await response.json();
      if (data.media) {
        setMedia(data.media);
      }
    } catch (error) {
      console.error('Error fetching media:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async (mediaId) => {
    setLoadingComments(true);
    try {
      const response = await fetch(`${API_URL}/api/media/${mediaId}/comments`);
      const data = await response.json();
      setComments(data.comments || []);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleLike = async (mediaItem) => {
    if (!currentUser) {
      alert('Please sign in to like photos');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/media/${mediaItem.media_id}/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          user_id: currentUser.user_id || currentUser.player_id,
          user_name: currentUser.name || currentUser.nickname || 'Someone'
        })
      });
      const data = await response.json();
      
      if (!response.ok) {
        console.error('Like error:', data);
        return;
      }
      
      // Update media state with the returned likes count
      setMedia(media.map(m => 
        m.media_id === mediaItem.media_id 
          ? { 
              ...m, 
              likes: data.likes,
              liked_by: data.liked 
                ? [...(m.liked_by || []), currentUser.user_id || currentUser.player_id] 
                : (m.liked_by || []).filter(id => id !== (currentUser.user_id || currentUser.player_id))
            }
          : m
      ));
      
      // Update selected media if open
      if (selectedMedia?.media_id === mediaItem.media_id) {
        setSelectedMedia(prev => ({
          ...prev,
          likes: data.likes,
          liked_by: data.liked 
            ? [...(prev.liked_by || []), currentUser.user_id || currentUser.player_id] 
            : (prev.liked_by || []).filter(id => id !== (currentUser.user_id || currentUser.player_id))
        }));
      }
    } catch (error) {
      console.error('Error liking media:', error);
    }
  };

  const handleAddComment = async () => {
    if (!currentUser) {
      alert('Please sign in to comment');
      return;
    }
    if (!newComment.trim() || !selectedMedia) return;

    setSubmittingComment(true);
    try {
      const response = await fetch(`${API_URL}/api/media/${selectedMedia.media_id}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: newComment.trim(),
          author_id: currentUser.user_id,
          author_name: currentUser.name,
          author_picture: currentUser.picture
        })
      });
      const data = await response.json();
      
      if (data.comment) {
        setComments([data.comment, ...comments]);
        setNewComment('');
        
        // Update comment count
        setSelectedMedia(prev => ({
          ...prev,
          comment_count: (prev.comment_count || 0) + 1
        }));
        setMedia(media.map(m => 
          m.media_id === selectedMedia.media_id 
            ? { ...m, comment_count: (m.comment_count || 0) + 1 }
            : m
        ));
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!currentUser) return;
    
    try {
      await fetch(`${API_URL}/api/media/comments/${commentId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ author_id: currentUser.user_id })
      });
      
      setComments(comments.filter(c => c.comment_id !== commentId));
      
      // Update comment count
      setSelectedMedia(prev => ({
        ...prev,
        comment_count: Math.max((prev.comment_count || 1) - 1, 0)
      }));
      setMedia(media.map(m => 
        m.media_id === selectedMedia.media_id 
          ? { ...m, comment_count: Math.max((m.comment_count || 1) - 1, 0) }
          : m
      ));
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const openMediaModal = (item) => {
    setSelectedMedia(item);
    fetchComments(item.media_id);
  };

  const closeMediaModal = () => {
    setSelectedMedia(null);
    setComments([]);
    setNewComment('');
  };

  const isLiked = (item) => {
    if (!currentUser) return false;
    const userId = currentUser.user_id || currentUser.player_id;
    return (item.liked_by || []).includes(userId);
  };

  const categories = [
    { id: 'all', label: 'All Media' },
    { id: 'nyaces', label: 'NY Aces' },
    { id: 'sponsored', label: 'Sponsored' },
    { id: 'community', label: 'Community' },
  ];

  const filteredMedia = media.filter(item => {
    if (selectedCategory === 'all') return true;
    if (selectedCategory === 'nyaces') return item.team_name === 'NY Aces';
    if (selectedCategory === 'sponsored') return item.is_sponsored;
    if (selectedCategory === 'community') return !item.is_sponsored;
    return true;
  });

  const getMediaUrl = (item) => {
    const url = item?.url || item?.file_path || '';
    if (url && (url.startsWith('/api') || url.startsWith('/uploads'))) {
      return `${API_URL}${url}`;
    }
    return url || '';
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-[#0a0b0c]">
      <Header />
      
      <main className="pt-32 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-white/60 hover:text-white mb-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
            <h1 className="text-4xl font-black text-white uppercase">GWINS Army Gallery</h1>
            <p className="text-white/60 mt-2">Photos and videos from our community</p>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map(cat => (
              <Button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                variant={selectedCategory === cat.id ? "default" : "outline"}
                className={selectedCategory === cat.id 
                  ? "bg-[#00d4aa] text-[#0a0b0c] hover:bg-[#33e0bb]" 
                  : "border-white/20 text-white/70 hover:text-white hover:bg-white/5"
                }
              >
                {cat.label}
              </Button>
            ))}
          </div>

          {/* Gallery Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-12 h-12 border-4 border-[#00d4aa] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredMedia.length === 0 ? (
            <div className="text-center py-20">
              <ImageIcon className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <p className="text-white/40">No media found in this category</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredMedia.map(item => (
                <div 
                  key={item.media_id} 
                  className="relative group cursor-pointer aspect-square rounded-xl overflow-hidden bg-[#131416]"
                  onClick={() => openMediaModal(item)}
                >
                  {item.type === 'video' ? (
                    <div className="w-full h-full bg-[#131416] flex items-center justify-center">
                      <Video className="w-12 h-12 text-white/40" />
                    </div>
                  ) : (
                    <img
                      src={getMediaUrl(item)}
                      alt={item.caption || 'Gallery media'}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/400x400?text=Image+Not+Found';
                      }}
                    />
                  )}
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0b0c] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  {/* Sponsored badge */}
                  {item.is_sponsored && (
                    <div className="absolute top-2 left-2">
                      <Badge className="bg-[#00d4aa] text-[#0a0b0c] font-bold">
                        <Star className="w-3 h-3 mr-1" />
                        SPONSORED
                      </Badge>
                    </div>
                  )}
                  
                  {/* Stats overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex items-center gap-4 text-white text-sm">
                      <span className="flex items-center gap-1">
                        <Heart className={`w-4 h-4 ${isLiked(item) ? 'fill-red-500 text-red-500' : ''}`} />
                        {item.likes || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageCircle className="w-4 h-4" />
                        {item.comment_count || 0}
                      </span>
                    </div>
                    {item.team_name && (
                      <p className="text-white/80 text-xs mt-1">{item.team_name}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Media Modal with Likes & Comments */}
      {selectedMedia && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={closeMediaModal}
        >
          <div 
            className="bg-[#131416] rounded-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col md:flex-row"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Image/Video */}
            <div className="md:w-2/3 bg-black flex items-center justify-center">
              {selectedMedia.type === 'video' ? (
                <video 
                  src={getMediaUrl(selectedMedia)} 
                  controls 
                  className="max-w-full max-h-[60vh] md:max-h-[80vh]"
                />
              ) : (
                <img
                  src={getMediaUrl(selectedMedia)}
                  alt={selectedMedia.caption || 'Gallery media'}
                  className="max-w-full max-h-[60vh] md:max-h-[80vh] object-contain"
                />
              )}
            </div>

            {/* Info & Comments Panel */}
            <div className="md:w-1/3 flex flex-col max-h-[40vh] md:max-h-[90vh]">
              {/* Header */}
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <div>
                  <p className="text-white font-semibold">{selectedMedia.team_name || 'Community'}</p>
                  {selectedMedia.caption && (
                    <p className="text-white/60 text-sm">{selectedMedia.caption}</p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={closeMediaModal}
                  className="text-white/60 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Actions */}
              <div className="p-4 border-b border-white/10 flex items-center gap-6">
                <button 
                  onClick={() => handleLike(selectedMedia)}
                  className="flex items-center gap-2 text-white hover:text-red-400 transition-colors"
                >
                  <Heart className={`w-6 h-6 ${isLiked(selectedMedia) ? 'fill-red-500 text-red-500' : ''}`} />
                  <span className="font-semibold">{selectedMedia.likes || 0}</span>
                </button>
                <div className="flex items-center gap-2 text-white/60">
                  <MessageCircle className="w-6 h-6" />
                  <span>{selectedMedia.comment_count || 0}</span>
                </div>
              </div>

              {/* Comments */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {loadingComments ? (
                  <div className="flex justify-center py-4">
                    <div className="w-6 h-6 border-2 border-[#00d4aa] border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : comments.length === 0 ? (
                  <p className="text-white/40 text-center text-sm">No comments yet. Be the first!</p>
                ) : (
                  comments.map(comment => (
                    <div key={comment.comment_id} className="flex gap-3">
                      <div className="flex-shrink-0">
                        {comment.author_picture ? (
                          <img 
                            src={comment.author_picture} 
                            alt="" 
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-[#1a1b1e] flex items-center justify-center">
                            <span className="text-white/60 text-xs">{comment.author_name?.[0]?.toUpperCase()}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-white font-semibold text-sm">{comment.author_name}</span>
                          <span className="text-white/40 text-xs">{formatTime(comment.created_at)}</span>
                        </div>
                        <p className="text-white/80 text-sm mt-1">{comment.content}</p>
                      </div>
                      {currentUser?.user_id === comment.author_id && (
                        <button
                          onClick={() => handleDeleteComment(comment.comment_id)}
                          className="text-white/40 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Add Comment */}
              <div className="p-4 border-t border-white/10">
                {currentUser ? (
                  <div className="flex gap-2">
                    <Input
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add a comment..."
                      className="bg-[#1a1b1e] border-white/10 text-white flex-1"
                      onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                    />
                    <Button
                      onClick={handleAddComment}
                      disabled={!newComment.trim() || submittingComment}
                      className="bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c]"
                    >
                      {submittingComment ? (
                        <div className="w-4 h-4 border-2 border-[#0a0b0c] border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                ) : (
                  <p className="text-white/40 text-sm text-center">
                    <button 
                      onClick={() => navigate('/community')}
                      className="text-[#00d4aa] hover:underline"
                    >
                      Sign in
                    </button>
                    {' '}to like and comment
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Gallery;
